/**
 * @module main
 * @description Entry point. Order matters, and is the point of this file:
 *   1. φ tokens are written to :root (no scale exists before this).
 *   2. Web fonts are confirmed loaded BEFORE the first paint of the app, so a
 *      late font never swaps in and reflows the layout (prevention over cure).
 *   3. The app mounts.
 *
 * Fonts are self-hosted via @fontsource (no third-party request), matching
 * afj-pledge-addon.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource/source-serif-4/400.css";
import "@fontsource/source-serif-4/600.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/700.css";
import "./design/tokens.css";
import "./design/motion.css";
// BUG FIX: base.css must load BEFORE any component CSS.
//   What caused this: it was imported at the end of app/App.tsx, after the component
//   imports, so generic rules (.kicker → bronze) overrode component rules
//   (.hero__kicker → bright gold) of equal specificity: 2.22:1 instead of 6.23:1.
//   Why we chose it: cascade order = global layer first, components after, fixed at the
//   single entry point rather than by raising selector specificity.
//   Side-effects: none intended; every component rule now wins over base rules as designed.
import "./design/base.css";
import { applyGoldenTokens } from "./design/goldenGeometry";
import { App } from "./app/App";

/** Every face the UI renders, as CSS font shorthands for document.fonts.load. */
const FONT_FACES: readonly string[] = [
    '400 1em "Source Serif 4"', '600 1em "Source Serif 4"',
    '400 1em "DM Sans"', '500 1em "DM Sans"', '700 1em "DM Sans"',
];

/**
 * Upper bound on waiting for fonts. On a very slow link we render with the
 * owned fallback stack (tokens.css --font-*) rather than show a blank page;
 * that is the one case where a late swap can still occur.
 */
const FONT_WAIT_MS: number = 2500;

/** @returns once all faces are loaded, or after FONT_WAIT_MS, whichever is first. */
async function fontsReady(): Promise<void> {
    if (!("fonts" in document)) return;
    const loaded: Promise<unknown> = Promise.all(FONT_FACES.map((face: string) => document.fonts.load(face)));
    const timeout: Promise<void> = new Promise<void>((resolve) => window.setTimeout(resolve, FONT_WAIT_MS));
    try {
        await Promise.race([loaded, timeout]);
    } catch {
        // A face failed to load: the fallback stack renders; nothing to recover.
    }
}

const rootElement: HTMLElement | null = document.getElementById("root");
if (rootElement === null) {
    // index.html always ships #root; reaching this means the HTML was edited.
    throw new Error("[main] #root element missing from index.html.");
}

applyGoldenTokens();

void fontsReady().then(() => {
    createRoot(rootElement).render(
        <StrictMode>
            <App />
        </StrictMode>,
    );
});
